import { token } from '@/infrastructure/container';
import { type EpochDay, MS_PER_DAY } from '@/domain/beforered';

export type DateFormat =
  | { kind: 'iso' }
  | { kind: 'dmy' }
  | { kind: 'mdy' }
  | { kind: 'excel-serial' }
  | { kind: 'unix-seconds' }
  | { kind: 'unix-millis' }
  | { kind: 'ambiguous'; pending: true };

export interface DateDetection {
  format: DateFormat;
  twoDigitYear: boolean;
  unparseableCount: number;
  sampleSize: number;
}

export interface IDateParser {
  detect(samples: readonly string[]): DateDetection;
  parseWithFormat(raw: string, format: DateFormat): EpochDay | null;
}

export const TOKEN_IDateParser = token<IDateParser>('IDateParser');

const ISO_RE = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/;
const DMY_OR_MDY_RE = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/;

const utcDay = (year: number, monthIndex: number, day: number): EpochDay =>
  Math.floor(Date.UTC(year, monthIndex, day) / MS_PER_DAY);

const expandTwoDigitYear = (yy: number): number => (yy <= 29 ? 2000 + yy : 1900 + yy);

export class DateParser implements IDateParser {
  detect(samples: readonly string[]): DateDetection {
    const trimmed = samples.map((s) => s.trim()).filter((s) => s.length > 0);
    const sampleSize = trimmed.length;
    if (sampleSize === 0) {
      return { format: { kind: 'iso' }, twoDigitYear: false, unparseableCount: 0, sampleSize: 0 };
    }

    const allNumeric = trimmed.every((s) => /^-?\d+(\.\d+)?$/.test(s));
    if (allNumeric) {
      const nums = trimmed.map(Number);
      const minN = Math.min(...nums);
      const maxN = Math.max(...nums);
      if (minN >= 25_000 && maxN <= 80_000) {
        return {
          format: { kind: 'excel-serial' },
          twoDigitYear: false,
          unparseableCount: 0,
          sampleSize,
        };
      }
      if (minN >= 1_000_000_000 && maxN <= 9_999_999_999) {
        return {
          format: { kind: 'unix-seconds' },
          twoDigitYear: false,
          unparseableCount: 0,
          sampleSize,
        };
      }
      if (minN >= 1_000_000_000_000 && maxN <= 9_999_999_999_999) {
        return {
          format: { kind: 'unix-millis' },
          twoDigitYear: false,
          unparseableCount: 0,
          sampleSize,
        };
      }
    }

    const isoMatches = trimmed.filter((s) => ISO_RE.test(s));
    if (isoMatches.length === trimmed.length) {
      return { format: { kind: 'iso' }, twoDigitYear: false, unparseableCount: 0, sampleSize };
    }

    const dmyMatches = trimmed
      .map((s) => DMY_OR_MDY_RE.exec(s))
      .filter((m): m is RegExpExecArray => m !== null);
    if (dmyMatches.length === trimmed.length) {
      let dmyEvidence = false;
      let mdyEvidence = false;
      let twoDigitYear = false;
      for (const m of dmyMatches) {
        const a = Number(m[1]);
        const b = Number(m[2]);
        const yRaw = m[3];
        if (yRaw.length === 2) twoDigitYear = true;
        if (a > 12) dmyEvidence = true;
        else if (b > 12) mdyEvidence = true;
      }
      if (dmyEvidence && !mdyEvidence)
        return { format: { kind: 'dmy' }, twoDigitYear, unparseableCount: 0, sampleSize };
      if (mdyEvidence && !dmyEvidence)
        return { format: { kind: 'mdy' }, twoDigitYear, unparseableCount: 0, sampleSize };
      return {
        format: { kind: 'ambiguous', pending: true },
        twoDigitYear,
        unparseableCount: 0,
        sampleSize,
      };
    }

    const unparseableCount = trimmed.filter((s) => {
      return !(ISO_RE.test(s) || DMY_OR_MDY_RE.test(s) || /^-?\d+(\.\d+)?$/.test(s));
    }).length;
    return {
      format: { kind: 'iso' },
      twoDigitYear: false,
      unparseableCount,
      sampleSize,
    };
  }

  parseWithFormat(raw: string, format: DateFormat): EpochDay | null {
    const s = raw.trim();
    if (s.length === 0) return null;

    if (format.kind === 'ambiguous') return null;

    if (format.kind === 'excel-serial') {
      const n = Number(s);
      if (!Number.isFinite(n)) return null;
      // Excel epoch 1899-12-30 UTC; day 1 = 1900-01-01.
      return Math.floor(n) - 25569;
    }
    if (format.kind === 'unix-seconds') {
      const n = Number(s);
      if (!Number.isFinite(n)) return null;
      return Math.floor(n / 86_400);
    }
    if (format.kind === 'unix-millis') {
      const n = Number(s);
      if (!Number.isFinite(n)) return null;
      return Math.floor(n / MS_PER_DAY);
    }
    if (format.kind === 'iso') {
      const m = ISO_RE.exec(s);
      if (!m) return null;
      return utcDay(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    }
    const m = DMY_OR_MDY_RE.exec(s);
    if (!m) return null;
    const a = Number(m[1]);
    const b = Number(m[2]);
    let y = Number(m[3]);
    if (m[3].length === 2) y = expandTwoDigitYear(y);
    if (format.kind === 'dmy') return utcDay(y, b - 1, a);
    return utcDay(y, a - 1, b);
  }
}
